import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Inbox, Trash2, Lock, LogOut } from "lucide-react";

const SKEY = "pechespot_admin_key";

export default function AdminMessagesPage() {
  const [key, setKey] = useState(sessionStorage.getItem(SKEY) || "");
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(null);

  const load = useCallback(async (k) => {
    try {
      const r = await api.get("/contact/messages", { headers: { "X-Admin-Key": k } });
      sessionStorage.setItem(SKEY, k);
      setKey(k);
      setMessages(r.data);
    } catch {
      sessionStorage.removeItem(SKEY);
      setKey("");
      toast.error("Clé administrateur invalide");
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    try {
      await api.delete(`/contact/messages/${id}`, { headers: { "X-Admin-Key": key } });
      setMessages(messages.filter((m) => m.id !== id));
      toast.success("Message supprimé");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const logout = () => { sessionStorage.removeItem(SKEY); setKey(""); setMessages(null); };

  if (!key || !messages)
    return (
      <form onSubmit={(e) => { e.preventDefault(); load(input); }} className="max-w-sm mx-auto px-4 py-24 space-y-4" data-testid="admin-login">
        <Lock className="w-8 h-8 text-emerald-700" />
        <h1 className="font-display text-2xl font-bold text-emerald-950">Messages reçus</h1>
        <Input type="password" placeholder="Clé administrateur" value={input} onChange={(e) => setInput(e.target.value)} data-testid="admin-key-input" className="rounded-xl" />
        <button type="submit" data-testid="admin-key-submit" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3 rounded-full">Accéder</button>
      </form>
    );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-14" data-testid="admin-messages">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl font-extrabold text-emerald-950 flex items-center gap-3">
          <Inbox className="w-7 h-7 text-emerald-600" /> Messages ({messages.length})
        </h1>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-emerald-800">
          <LogOut className="w-4 h-4" /> Quitter
        </button>
      </div>
      {messages.length === 0 && <p className="text-slate-500" data-testid="admin-no-messages">Aucun message pour le moment.</p>}
      <div className="space-y-4">
        {messages.map((m) => (
          <div key={m.id} className="rounded-2xl bg-white border border-emerald-900/10 p-5" data-testid={`admin-message-${m.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-semibold text-emerald-950">{m.subject}</p>
                <p className="text-xs text-slate-500">
                  {m.name} · <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`} className="text-emerald-700 underline">{m.email}</a> · {new Date(m.created_at).toLocaleString("fr-FR")}
                </p>
              </div>
              <button onClick={() => remove(m.id)} data-testid={`admin-delete-${m.id}`} className="p-2 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50" title="Supprimer">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
